// What a skin IS, and which one a document wears.
//
// A BUILT-IN skin is code: a stylesheet in web/skins/<id>/skin.css, compiled into the build and scoped
// under `:root[data-skin='<id>']`, with its structural rules in one of the family stylesheets
// (`[data-skin-family='…']`) so a customer skin derived from it inherits the same structure. Because every
// built-in is already in the browser, switching between them is only an attribute write.
//
// A CUSTOMER skin is instance data: `<dataDir>/skins/<custom-id>/`, validated and rendered into CSS by the
// daemon (src/shared/skinStore.ts), which serves it by content digest. This module therefore never assumes
// the set of ids is known at build time. One catalogue — the daemon's, falling back to the built-ins this
// build compiles — is the single answer to "which designs exist", and everything that resolves a skin, lists
// one or names one reads it.
//
// This module is pure and safe to import from a client component. Reading the operator's env default is
// deliberately NOT here for exactly that reason — see lib/skinEnv.ts.

import { BUILTIN_SKIN_PAINT } from './builtinSkinPaint.generated';
import type { LocaleDict } from './i18n/types';

/** Every skin compiled into this build. A new built-in needs: a folder web/skins/<name>/ with skin.css, an
 *  @import line in web/skins/index.css, and its name here — a contract test holds the three in sync. */
export const SKINS = ['studio-light', 'studio-oled', 'umber-night', 'studio-graphite'] as const;
export type BuiltinSkinName = (typeof SKINS)[number];

/** A skin id as the instance catalogue reports it: one of the built-ins above, or a `custom-…` package under
 *  the instance's skin root. The grammar is the directory-name rule the daemon writes, and the `custom-`
 *  prefix is reserved, so a customer skin can never shadow a compiled one. */
export type SkinId = string;
const CUSTOM_SKIN_ID_RE = /^custom-[a-z0-9][a-z0-9-]{0,56}$/;

/** Skins that are variants of ONE design rather than separate designs. Every member — `studio-light`,
 *  `studio-oled`, `umber-night` and `studio-graphite` — shares every structural rule and differs only in its token block, so
 *  anything that reasons about the design — shared stylesheets, docs, a future grouped picker — reasons
 *  about the family. A customer skin inherits its base's family; it cannot choose another. */
export type SkinFamily = 'studio';

/** The dictionary key holding a built-in skin's human name. Skins are named in `common.skinNames` rather
 *  than by their id: an id is a directory and an attribute value, and `umber-night` is not a name to show
 *  anyone. A customer skin brings its own literal name instead — the operator wrote it, and the daemon
 *  bounded it. */
type SkinNameKey = keyof LocaleDict['common']['skinNames'];

export interface SkinDefinition {
  readonly id: BuiltinSkinName;
  readonly family: SkinFamily;
  readonly nameKey: SkinNameKey;
}

/** Every compiled skin's metadata. Declared as a total Record over BuiltinSkinName so the compiler — not
 *  review — rejects a skin added to SKINS with no definition, and a definition for a skin that does not
 *  exist. */
export const SKIN_DEFINITIONS: Record<BuiltinSkinName, SkinDefinition> = {
  'studio-light': { id: 'studio-light', family: 'studio', nameKey: 'studioLight' },
  'studio-oled': { id: 'studio-oled', family: 'studio', nameKey: 'studioOled' },
  'umber-night': { id: 'umber-night', family: 'studio', nameKey: 'umberNight' },
  'studio-graphite': { id: 'studio-graphite', family: 'studio', nameKey: 'studioGraphite' },
};

/** Every skin belonging to a family, in SKINS order. Derived, never restated: a skin's family is stated
 *  once, in its definition, so membership cannot drift away from it. */
function skinsInFamily(family: SkinFamily): BuiltinSkinName[] {
  return SKINS.filter((id) => SKIN_DEFINITIONS[id].family === family);
}

/** Stylesheets under web/skins/ holding structure shared by a whole family, keyed by the family that owns
 *  them. Paths are the ONLY thing declared by hand here — a file location cannot be derived from types —
 *  and a family with no shared stylesheet simply has no entry.
 *
 *  These files are the reason this declaration exists at all. A per-skin stylesheet is scoped by its own
 *  directory name, which tests/lib/skins.test.ts can check without being told anything; a shared file has
 *  no such name, so the set of families (and variant bases) it is allowed to target has to be stated
 *  somewhere the guard can read. */
const SHARED_STYLESHEET_PATHS = {
  studio: ['studio/shared.css', 'studio/surfaces.css', 'studio/workbench.css'],
} as const satisfies Partial<Record<SkinFamily, readonly string[]>>;

export interface SkinFamilySheets {
  readonly family: SkinFamily;
  /** Exactly the skins a shared stylesheet of this family may target — derived from SKIN_DEFINITIONS, so
   *  an id that is not a real BuiltinSkinName is a compile error rather than a test failure. */
  readonly members: readonly BuiltinSkinName[];
  /** Paths relative to web/skins/. */
  readonly sharedStylesheets: readonly string[];
}

/**
 * Test seam: web/tests/lib/skins.test.ts and designTokens.test.ts enumerate the production family registry.
 * @internal
 */
export const SKIN_FAMILY_SHEETS: readonly SkinFamilySheets[] = (
  Object.keys(SHARED_STYLESHEET_PATHS) as (keyof typeof SHARED_STYLESHEET_PATHS)[]
).map((family) => ({
  family,
  members: skinsInFamily(family),
  sharedStylesheets: SHARED_STYLESHEET_PATHS[family],
}));

/** THE FLOOR UNDER EVERY RESOLUTION, and the reason `resolveSkin` cannot return nothing.
 *
 *  Every path that used to end at "no skin" ends here instead: nothing chosen, a choice the admin has
 *  revoked, a stored name this instance no longer has, an unset or malformed ELOWEN_SKIN. The document
 *  therefore ALWAYS carries `data-skin`; the unattributed pre-skins design no longer exists. */
export const DEFAULT_SKIN: BuiltinSkinName = 'studio-light';

// ---------------------------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------------------------

import type { SkinColorScheme, SkinCatalogueEntry, SkinCatalogue } from '../../src/shared/wireContract';
export type { SkinColorScheme, SkinCatalogue } from '../../src/shared/wireContract';

/** What the browser chrome and the anti-FOUC fill need, whichever catalogue answered. */
export interface SkinPaint {
  readonly background: string;
  readonly colorScheme: SkinColorScheme;
}

/** The catalogue of an instance whose daemon cannot answer — it is down, too old, or the payload was
 *  refused. The built-ins are compiled into this bundle, so a document still wears a real design and still
 *  paints; nothing that is not in this build can be offered, and no custom skin is invented. */
export const builtinCatalogue = (): SkinCatalogue => ({
  revision: 'builtin',
  skins: SKINS.map((id) => ({
    id,
    source: 'builtin' as const,
    base: id,
    colorScheme: BUILTIN_SKIN_PAINT[id]!.colorScheme,
    background: BUILTIN_SKIN_PAINT[id]!.background,
  })),
});

/** Values a catalogue may carry into a DOM attribute, an inline style or a stylesheet URL. The daemon
 *  validates all of them; this re-checks the SHAPES because the payload crosses a process boundary and the
 *  background ends up in a `style` attribute. Anything else refuses the whole payload, so a half-read
 *  catalogue can never be offered as if it were complete. */
const BACKGROUND_RE = /^[#A-Za-z0-9(),./% +-]{1,200}$/;

/** Read the daemon's catalogue payload, or null when it is not one. */
export function parseSkinCatalogue(body: unknown): SkinCatalogue | null {
  if (!body || typeof body !== 'object') return null;
  const raw = body as { revision?: unknown; skins?: unknown };
  if (typeof raw.revision !== 'string' || !Array.isArray(raw.skins) || raw.skins.length === 0) return null;
  const skins: SkinCatalogueEntry[] = [];
  for (const item of raw.skins) {
    if (!item || typeof item !== 'object') return null;
    const entry = item as Record<string, unknown>;
    const id = entry.id;
    const base = entry.base;
    if (typeof id !== 'string' || !isSkinId(id)) return null;
    if (typeof base !== 'string' || !(SKINS as readonly string[]).includes(base)) return null;
    if (entry.source !== 'builtin' && entry.source !== 'custom') return null;
    if (entry.colorScheme !== 'light' && entry.colorScheme !== 'dark') return null;
    if (typeof entry.background !== 'string' || !BACKGROUND_RE.test(entry.background)) return null;
    const displayName = entry.displayName === undefined ? undefined : entry.displayName;
    if (displayName !== undefined && typeof displayName !== 'string') return null;
    const fontFamily = entry.fontFamily;
    if (fontFamily !== undefined && (typeof fontFamily !== 'string' || !/^[A-Za-z][A-Za-z0-9 -]{0,59}$/.test(fontFamily))) return null;
    const digest = entry.digest === undefined ? undefined : entry.digest;
    if (digest !== undefined && (typeof digest !== 'string' || !/^[0-9a-f]{16}$/.test(digest))) return null;
    if (entry.source === 'custom' && digest === undefined) return null;
    skins.push({
      id, source: entry.source, base, colorScheme: entry.colorScheme, background: entry.background,
      ...(displayName === undefined ? {} : { displayName }),
      ...(digest === undefined ? {} : { digest }),
      ...(fontFamily === undefined ? {} : { fontFamily }),
    });
  }
  // Every built-in is always on offer: the allow-list and ELOWEN_SKIN may legitimately name one, and a
  // catalogue that omitted it would silently make a compiled design unreachable.
  for (const id of SKINS) if (!skins.some((entry) => entry.id === id)) return null;
  return { revision: raw.revision, skins };
}

/** The entry for an id, or null when this instance does not have that skin. */
export function catalogueEntry(catalogue: SkinCatalogue, id: string | null | undefined): SkinCatalogueEntry | null {
  return id ? catalogue.skins.find((entry) => entry.id === id) ?? null : null;
}

/** Every id the catalogue offers. */
const catalogueIds = (catalogue: SkinCatalogue): Set<string> => new Set(catalogue.skins.map((entry) => entry.id));

/** Whether a stored or configured value could be a skin id at all — a compiled one or a customer package.
 *  Grammar only: whether this INSTANCE has it is a catalogue question. */
export const isSkinId = (value: string | null | undefined): value is SkinId =>
  !!value && ((SKINS as readonly string[]).includes(value) || CUSTOM_SKIN_ID_RE.test(value));

/** Which family a skin belongs to, and therefore which structural stylesheet applies to it. A customer
 *  skin's family is its base's, never its own. An id this instance does not have falls to the floor's
 *  family, exactly as the id itself falls to DEFAULT_SKIN. */
export function skinFamily(id: string | null | undefined, catalogue: SkinCatalogue): SkinFamily {
  return SKIN_DEFINITIONS[baseOf(catalogueEntry(catalogue, id)) as BuiltinSkinName].family;
}

/** The built-in a skin derives from. Anything unresolvable reads as the floor, which is a real compiled
 *  design with a real palette. */
export function baseOf(entry: SkinCatalogueEntry | null): string {
  return entry && (SKINS as readonly string[]).includes(entry.base) ? entry.base : DEFAULT_SKIN;
}

/** The first-frame paint of a skin. Every built-in is present in every catalogue this app accepts, so the
 *  compiled projection only answers for an id the catalogue does not have. */
export function skinPaint(id: string | null | undefined, catalogue: SkinCatalogue): SkinPaint {
  const entry = catalogueEntry(catalogue, id);
  if (entry) return { background: entry.background, colorScheme: entry.colorScheme };
  const fallback = BUILTIN_SKIN_PAINT[DEFAULT_SKIN]!;
  return { background: fallback.background, colorScheme: fallback.colorScheme };
}

/** Where the browser fetches a customer skin's stylesheet — through the same-origin BFF proxy, like every
 *  other daemon path. Null for a built-in, whose CSS is already in the bundle. */
export function skinStylesheetUrl(entry: SkinCatalogueEntry | null): string | null {
  if (!entry || entry.source !== 'custom' || !entry.digest) return null;
  return `/api/public/skins/css/${entry.id}/${entry.digest}.css`;
}

/** The display name of a skin: a translated name for a compiled design, the operator's own text for a
 *  customer skin. A skin this instance does not have shows the design actually used as the floor. */
export function skinDisplayName(t: LocaleDict, id: string | null | undefined, catalogue: SkinCatalogue): string {
  const entry = catalogueEntry(catalogue, id);
  if (entry?.displayName) return entry.displayName;
  return t.common.skinNames[SKIN_DEFINITIONS[baseOf(entry) as BuiltinSkinName].nameKey];
}

// ---------------------------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------------------------

/** The designs an account may actually pick, in operator order and without duplicates. An id the instance
 *  does not have is dropped here rather than offered as a choice that does nothing — a name left behind by a
 *  skin that was removed, or one whose folder no longer validates. */
export function allowedSkinChoices(configured: readonly string[] | null | undefined, catalogue: SkinCatalogue): string[] {
  if (!configured) return [];
  const available = catalogueIds(catalogue);
  const out: string[] = [];
  for (const name of configured) {
    if (!available.has(name) || out.includes(name)) continue;
    out.push(name);
  }
  return out;
}

/** Resolve any stored input to a skin this instance actually has. `fallback` is the operator's default and
 *  is honoured whether or not it is one of the offered choices; everything else lands on the floor. */
export function resolveSkin(
  chosen: string | null | undefined,
  allowed: readonly string[],
  fallback: string | null | undefined,
  catalogue: SkinCatalogue,
): string {
  const available = catalogueIds(catalogue);
  if (chosen && available.has(chosen) && allowed.includes(chosen)) return chosen;
  if (fallback && available.has(fallback)) return fallback;
  return DEFAULT_SKIN;
}

/** The choice corresponding to what is currently on screen, or null when that design is not selectable. */
export function currentSkinChoice(
  chosen: string | null | undefined,
  allowed: readonly string[],
  fallback: string | null | undefined,
  catalogue: SkinCatalogue,
): string | null {
  const available = catalogueIds(catalogue);
  if (chosen && available.has(chosen) && allowed.includes(chosen)) return chosen;
  const implied = fallback && available.has(fallback) ? fallback : DEFAULT_SKIN;
  return allowed.includes(implied) ? implied : null;
}

/** Advance through the allowed designs, wrapping at the end. */
export function nextSkinChoice(current: string | null, allowed: readonly string[]): string | null {
  if (allowed.length === 0) return null;
  const at = current ? allowed.indexOf(current) : -1;
  return allowed[(at + 1) % allowed.length]!;
}
